import { useEffect, useState } from 'react';

const KEY = 'numberFormat';

// 'us' -> 1,234.56   'ar' -> 1.234,56 (Argentina; same convention as Colombia, Chile, Brazil...)
export const NUMBER_FORMATS = {
  us: { label: 'US', example: '1,234.56', locale: 'en-US' },
  ar: { label: 'AR', example: '1.234,56', locale: 'es-AR' },
};

function read() {
  try {
    const m = document.cookie.match(new RegExp(`(?:^|;\\s*)${KEY}=([^;]*)`));
    const v = m ? decodeURIComponent(m[1]) : null;
    return v in NUMBER_FORMATS ? v : 'us';
  } catch {
    return 'us';
  }
}

function write(fmt) {
  const secure = window.location.protocol === 'https:' ? '; Secure' : '';
  document.cookie = `${KEY}=${encodeURIComponent(fmt)}; Max-Age=31536000; Path=/; SameSite=Lax${secure}`;
}

let current = read();
const cache = new Map();

export function getNumberFormat() {
  return current;
}

export function setNumberFormat(fmt) {
  if (!(fmt in NUMBER_FORMATS)) return;
  current = fmt;
  try { write(fmt); } catch { /* cookies blocked: applies for this session only */ }
  window.dispatchEvent(new Event('numberformatchange'));
}

// Formats a finite number with the active convention. minDigits defaults to maxDigits.
export function formatNumber(n, maxDigits = 0, minDigits = maxDigits) {
  const key = `${current}:${minDigits}:${maxDigits}`;
  let nf = cache.get(key);
  if (!nf) {
    nf = new Intl.NumberFormat(NUMBER_FORMATS[current].locale, {
      minimumFractionDigits: minDigits,
      maximumFractionDigits: maxDigits,
      useGrouping: 'always', // es locales otherwise skip the separator on 4-digit numbers
    });
    cache.set(key, nf);
  }
  return nf.format(n);
}

// Components that render formatted numbers re-render when this changes (used once, at the app root).
export function useNumberFormat() {
  const [fmt, setLocal] = useState(getNumberFormat);
  useEffect(() => {
    const onChange = () => setLocal(getNumberFormat());
    window.addEventListener('numberformatchange', onChange);
    return () => window.removeEventListener('numberformatchange', onChange);
  }, []);
  return [fmt, setNumberFormat];
}
